import React from 'react';
import cn from 'classnames';
import InfiniteScroll from 'react-infinite-scroller';

import MessageList from 'widget/components/MessageList';
import LoadingDots from 'widget/shared/ui/LoadingDots';
import ScrollToBottomButton from 'widget/components/ScrollToBottomButton';
import type { IMessage } from 'widget/types/messages';
import SubmissionForm from 'widget/components/SubmissionForm';
import { IWidgetChannels } from 'widget/entities/config';
import { LayoutVersion } from 'widget/types/common';
import useScrollbarCompensation from 'widget/hooks/useScrollbarCompensation';

import styles from './WidgetContent.module.scss';
import WidgetMobileChannels from './WidgetMobileChannels/WidgetMobileChannels';

const PADDING_X = 16;

const INFINITE_SCROLL_THRESHOLD = 50;

export type WidgetContentProps = {
  className?: string;
  messages?: IMessage[];
  isTyping?: boolean;
  hasMore?: boolean;
  onLoadHistory?: () => void;
  onRetry?: (messageId: string) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  hideMessages?: boolean;
  botAvatarUrl?: string | null;
  channels?: IWidgetChannels;
  layout?: LayoutVersion;
  showScrollButton?: boolean;
  unreadCount?: number;
  onScrollToBottom?: () => void;
};

const WidgetContent: React.FC<WidgetContentProps> = ({
  className,
  messages = [],
  isTyping = false,
  hasMore = false,
  onLoadHistory,
  onRetry,
  scrollContainerRef: scrollContainerRefProp,
  hideMessages = false,
  botAvatarUrl,
  channels,
  layout,
  showScrollButton = false,
  unreadCount = 0,
  onScrollToBottom
}) => {
  const ownScrollRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = scrollContainerRefProp ?? ownScrollRef;
  const isMobile = layout === LayoutVersion.MOBILE;

  useScrollbarCompensation(scrollContainerRef, PADDING_X);

  const handleLoad = React.useCallback(() => {
    if (onLoadHistory) {
      onLoadHistory();
    }
  }, [onLoadHistory]);

  return (
    <div className={cn(styles['content-wrapper'], className)}>
      {isMobile && <WidgetMobileChannels channels={channels} />}
      <div ref={scrollContainerRef} className={styles['content']}>
        <div className={styles['content__inner']}>
          <InfiniteScroll
            className={styles['content__scroll']}
            useWindow={false}
            isReverse
            hasMore={hasMore}
            initialLoad={false}
            loadMore={handleLoad}
            threshold={INFINITE_SCROLL_THRESHOLD}
            getScrollParent={() => scrollContainerRef.current}
            loader={
              <div key="loader" className={styles['content__loading']}>
                <LoadingDots />
              </div>
            }
          >
            {!hideMessages && (
              <MessageList
                messages={messages}
                onRetry={onRetry}
                isTyping={isTyping}
                botAvatarUrl={botAvatarUrl}
                hasMore={hasMore}
              />
            )}
            <SubmissionForm />
          </InfiniteScroll>
        </div>
      </div>

      {showScrollButton && onScrollToBottom && (
        <ScrollToBottomButton
          count={unreadCount}
          onClick={onScrollToBottom}
          className={styles['content-wrapper__scroll-button']}
        />
      )}
    </div>
  );
};

export default React.memo(WidgetContent);
